import React from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { formatMoney, getTenantDomain, handleImageError } from "../../helper";
import { nightsBetween } from "../../helper/stayRange";

const showDate = (date) => (date ? moment(date).format("DD.MM.YYYY") : "");

// Summary of the payment step: the stay that is booked and one list of what
// it costs, with the same total as the booking card of the property page.
const BookingSummary = ({
  selectedProperty,
  tenant,
  arrivalDate,
  departureDate,
  guests,
  price,
  currency,
}) => {
  const { t } = useTranslation();
  const money = (amount) => formatMoney(amount, 2, currency ?? null);
  const nights = nightsBetween(
    arrivalDate ? moment(arrivalDate).toDate() : null,
    departureDate ? moment(departureDate).toDate() : null
  );
  const gallery = selectedProperty?.gallery;
  const image = gallery?.length
    ? `${getTenantDomain(tenant)}/tenancy/assets/properties/gallery/thumbnails/${gallery[0]?.filename}`
    : "https://via.placeholder.com/104";

  const label = (line) => {
    switch (line.type) {
      case "room":
        return [line.name, `${t("guests")}: ${line.guests}`, line.count > 1 ? `${line.count}x` : null]
          .filter(Boolean)
          .join(" · ");
      case "upsell":
        return `${line.name} ${line.count}x`;
      case "cleaning_fee":
        // Apartments with their own fee have a line each, the property fee none.
        return line.name
          ? [
              t("cleaning_fee_for", { name: line.name, defaultValue: "Cleaning · {{name}}" }),
              line.count > 1 ? `${line.count}x` : null,
            ]
              .filter(Boolean)
              .join(" · ")
          : t("cleaning_fee");
      case "city_tax":
        return t("city_tax");
      case "voucher":
        return line.percent ? `${t("voucher_discount")} (${line.percent}%)` : t("voucher_discount");
      default:
        return "";
    }
  };

  return (
    <div className='booking-summary'>
      <div className='booking-summary__property'>
        <img src={image} alt='' onError={handleImageError} />
        <p>{selectedProperty?.name}</p>
      </div>

      <dl className='booking-summary__stay'>
        <div>
          <dt>{t("arrival")}</dt>
          <dd>{showDate(arrivalDate)}</dd>
        </div>
        <div>
          <dt>{t("departure")}</dt>
          <dd>{showDate(departureDate)}</dd>
        </div>
        <div className='booking-summary__stay-facts'>
          {nights > 0 && (
            <span>{t("stay_nights", { count: nights, defaultValue: "Nights: {{count}}" })}</span>
          )}
          <span>{`${t("guests")}: ${guests}`}</span>
        </div>
      </dl>

      <div className='booking-summary__price'>
        <h4>{t("your_total")}</h4>
        <ul className='listUnstyled'>
          {price.lines.map((line, index) => (
            <li key={`${line.type}-${index}`} className={`booking-summary__line booking-summary__line--${line.type}`}>
              <span>{label(line)}</span>
              <span>{line.amount < 0 ? `-${money(-line.amount)}` : money(line.amount)}</span>
            </li>
          ))}
        </ul>
        <div className='booking-summary__total'>
          <span>{t("total")}</span>
          <strong>{money(price.total)}</strong>
        </div>
      </div>
    </div>
  );
};

export default BookingSummary;
